import apiClient from './client';

const isFileLike = (value) => {
  if (!value) return false;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  return false;
};

const buildServiceFormData = (data) => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(data || {})) {
    if (value === undefined) continue;

    if (key === 'image') {
      if (isFileLike(value)) {
        formData.append('service_image', value);
      } else if (value !== null) {
        formData.append('image', String(value));
      }
      continue;
    }

    if (key === 'availability') {
      if (value === null || value === '') {
        formData.append('availability', '');
      } else if (typeof value === 'object') {
        formData.append('availability', JSON.stringify(value));
      } else {
        formData.append('availability', String(value));
      }
      continue;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      formData.append(key, String(value));
    } else {
      formData.append(key, value);
    }
  }

  return formData;
};

const buildStaffFormData = (data) => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(data || {})) {
    if (value === undefined) continue;

    if (key === 'staff_image') {
      if (isFileLike(value)) {
        formData.append('staff_image', value);
      } else if (value !== null) {
        formData.append('staff_image', String(value));
      }
      continue;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      formData.append(key, String(value));
    } else {
      formData.append(key, value);
    }
  }

  return formData;
};

const buildPortfolioFormData = (data) => {
  const formData = new FormData();

  const { portfolio_images, ...rest } = data || {};

  for (const [key, value] of Object.entries(rest || {})) {
    if (value === undefined) continue;
    if (key === 'images') {
      if (value === null) continue;
      if (typeof value === 'string') {
        formData.append('images', value);
      } else if (Array.isArray(value)) {
        formData.append('images', JSON.stringify(value));
      } else {
        formData.append('images', JSON.stringify(value));
      }
      continue;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      formData.append(key, String(value));
    } else {
      formData.append(key, value);
    }
  }

  const files = Array.isArray(portfolio_images) ? portfolio_images : [];
  for (const f of files) {
    if (isFileLike(f)) {
      formData.append('portfolio_images', f);
    }
  }

  return formData;
};

export const businessAPI = {
  // Business CRUD
  getAll: (params = {}) => apiClient.get('/businesses', { params }),
  getById: (id) => apiClient.get(`/businesses/${id}`),
  getMyBusiness: () => apiClient.get('/businesses/me'),
  getFavorites: () => apiClient.get('/businesses/favorites'),
  addFavorite: (businessId) => apiClient.post(`/businesses/${businessId}/favorite`),
  removeFavorite: (businessId) => apiClient.delete(`/businesses/${businessId}/favorite`),
  create: (data) => apiClient.post('/businesses', data),
  update: (id, data) => apiClient.put(`/businesses/${id}`, data),
  delete: (id) => apiClient.delete(`/businesses/${id}`),
  
  // Staff Management
  getStaff: (businessId) => apiClient.get(`/businesses/${businessId}/staff`),
  addStaff: (businessId, data) => {
    if (isFileLike(data?.staff_image)) {
      return apiClient.post(`/businesses/${businessId}/staff`, buildStaffFormData(data));
    }
    return apiClient.post(`/businesses/${businessId}/staff`, data);
  },
  removeStaff: (businessId, staffId) => apiClient.delete(`/businesses/${businessId}/staff/${staffId}`),
  updateStaff: (businessId, staffId, data) => {
    if (isFileLike(data?.staff_image)) {
      return apiClient.put(`/businesses/${businessId}/staff/${staffId}`, buildStaffFormData(data));
    }
    return apiClient.put(`/businesses/${businessId}/staff/${staffId}`, data);
  },
  
  // Service Management
  getServices: (businessId) => apiClient.get(`/businesses/${businessId}/services`),
  createService: (businessId, data) => {
    if (isFileLike(data?.image)) {
      return apiClient.post(`/businesses/${businessId}/services`, buildServiceFormData(data));
    }
    return apiClient.post(`/businesses/${businessId}/services`, data);
  },
  updateService: (businessId, serviceId, data) => {
    if (isFileLike(data?.image)) {
      return apiClient.put(`/businesses/${businessId}/services/${serviceId}`, buildServiceFormData(data));
    }
    return apiClient.put(`/businesses/${businessId}/services/${serviceId}`, data);
  },
  deleteService: (businessId, serviceId) => apiClient.delete(`/businesses/${businessId}/services/${serviceId}`),
  
  // Working Hours
  getWorkingHours: (businessId) => apiClient.get(`/businesses/${businessId}/hours`),
  setWorkingHours: (businessId, data) => apiClient.put(`/businesses/${businessId}/hours`, data),

  // Portfolio
  getPortfolio: (businessId, params = {}) => apiClient.get(`/businesses/${businessId}/portfolio`, { params }),
  getPortfolioItemById: (businessId, portfolioId) => apiClient.get(`/businesses/${businessId}/portfolio/${portfolioId}`),
  createPortfolioItem: (businessId, data) => apiClient.post(`/businesses/${businessId}/portfolio`, buildPortfolioFormData(data)),
  updatePortfolioItem: (businessId, portfolioId, data) => apiClient.put(`/businesses/${businessId}/portfolio/${portfolioId}`, buildPortfolioFormData(data)),
  deletePortfolioItem: (businessId, portfolioId) => apiClient.delete(`/businesses/${businessId}/portfolio/${portfolioId}`),
  getPortfolioFeed: (params = {}) => apiClient.get('/businesses/portfolio', { params }),
  getPortfolioBusinesses: (params = {}) => apiClient.get('/businesses/portfolio/businesses', { params }),
};
